import { useTranslation } from "react-i18next";
import {
  Bot,
  Brain,
  Coins,
  FileEdit,
  FileInput,
  FileStack,
  GitBranch,
  ListChecks,
  SendHorizonal,
  ShieldCheck,
  Square,
  Upload,
  UserCog,
  UserPlus,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ProductNodeType } from "@/services/platformEnterprisesService";

interface PaletteItem {
  type: Exclude<ProductNodeType, "start">;
  icon: typeof FileEdit;
  iconClass: string;
  labelKey?: string;
  dataOverrides?: Record<string, unknown>;
}

interface PaletteGroup {
  labelKey: string;
  items: PaletteItem[];
}

const GROUPS: PaletteGroup[] = [
  {
    labelKey: "enterpriseProductBuilder.paletteGroupData",
    items: [
      { type: "precondition", icon: ShieldCheck, iconClass: "text-amber-600" },
      { type: "userForm", icon: FileEdit, iconClass: "text-sky-600" },
      { type: "userForm", icon: UserPlus, iconClass: "text-sky-600", labelKey: "participantForm", dataOverrides: { actorSource: "" } },
      { type: "documentCollection", icon: FileStack, iconClass: "text-purple-700" },
    ],
  },
  {
    labelKey: "enterpriseProductBuilder.paletteGroupFlow",
    items: [
      { type: "branch", icon: GitBranch, iconClass: "text-rose-600" },
      { type: "decision", icon: ListChecks, iconClass: "text-orange-600" },
      { type: "pricing", icon: Coins, iconClass: "text-emerald-600" },
      { type: "end", icon: Square, iconClass: "text-slate-600" },
    ],
  },
  {
    labelKey: "enterpriseProductBuilder.paletteGroupStaff",
    items: [
      { type: "staffApproval", icon: UserCog, iconClass: "text-violet-600" },
      { type: "staffAction", icon: Brain, iconClass: "text-violet-600" },
      { type: "staffAction", icon: Upload, iconClass: "text-violet-600", labelKey: "staffActionUpload", dataOverrides: { fileUpload: true } },
    ],
  },
  {
    labelKey: "enterpriseProductBuilder.paletteGroupIntegrations",
    items: [
      { type: "inviteActors", icon: Users, iconClass: "text-teal-600" },
      { type: "createRequest", icon: SendHorizonal, iconClass: "text-cyan-600" },
      { type: "internalRequest", icon: FileInput, iconClass: "text-indigo-600" },
      { type: "automation", icon: Bot, iconClass: "text-fuchsia-600" },
    ],
  },
];

interface ProductBuilderPaletteProps {
  onAdd: (type: ProductNodeType, dataOverrides?: Record<string, unknown>) => void;
}

export function ProductBuilderPalette({
  onAdd,
}: ProductBuilderPaletteProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col gap-0.5 p-2">
      <div className="px-2 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("enterpriseProductBuilder.paletteTitle")}
      </div>
      {GROUPS.map((group, gi) => (
        <div key={gi} className={gi > 0 ? "mt-2" : undefined}>
          <div className="px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
            {t(group.labelKey)}
          </div>
          {group.items.map((item, idx) => {
            const Icon = item.icon;
            const key = item.labelKey ?? item.type;
            return (
              <Button
                key={`${key}-${gi}-${idx}`}
                variant="ghost"
                size="sm"
                className="h-8 w-full justify-start gap-2 text-left transition-colors duration-150"
                onClick={() => onAdd(item.type, item.dataOverrides)}
              >
                <Icon className={cn("h-3.5 w-3.5 shrink-0", item.iconClass)} />
                <span className="text-xs font-medium">
                  {t(`enterpriseProductBuilder.nodeType.${key}`)}
                </span>
              </Button>
            );
          })}
        </div>
      ))}
      <div className="mt-auto px-2 pt-3 text-[11px] leading-snug text-muted-foreground">
        {t("enterpriseProductBuilder.deleteHint")}
      </div>
    </div>
  );
}
